import { ArrowRight, Check, CircleUser, Image, Megaphone, ReceiptText, X, File } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useGetCompaniesQuery } from 'src/redux/billing/billing.endpoints';
import { Account, Campaign } from '../../types/client-management.types';
import { useGetAccountsQuery, useGetCampaignsQuery } from '../../endpoints/accounts.endpoints';
import { Button } from 'src/components/ui/button';
import CompanyMultiSelector from './company-multi-selector';
import AccountMultiSelector from './account-multi-selector';
import CampaignMultiSelector from './campaign-multi-selector';
import ClientCreationMenuBar from './client-creation-menu-bar';
import ClientViewCustomize from './client-customization/client-view-customize';
import ClientUserTable from './client-customization/client-user-table';
import { useCreateClientMutation, useUpdateClientMutation, useUpdateClientAccessMutation, useGetClientAccessQuery, useDeleteClientAccessMutation, useGetOrganizationClientsQuery } from '../../endpoints/client.endpoints';
import { useDispatch } from 'react-redux';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { Company } from 'src/pages/billing/types/billing.types';
import { useUploadImageMutation } from 'src/redux/survey/survey.endpoints';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { FALLBACK_DEFAULT_CLIENT } from 'src/redux/organization/organization.types';

interface ClientCreationComponentProps {
  isCreate: boolean;
}

const ClientCreationComponent: React.FC<ClientCreationComponentProps> = ({
  isCreate,
}) => {
  const { clearClient, setClient, activeClient } = useActiveClient();

  useEffect(() => {
    if (isCreate) {
      clearClient();
    }
  }, [isCreate]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [createClient, { isLoading: isLoadingCreateClient }] = useCreateClientMutation();
  const [updateClient, { isLoading: isLoadingUpdateClient }] = useUpdateClientMutation();
  const [updateClientAccess, { isLoading: isLoadingUpdateClientAccess }] = useUpdateClientAccessMutation();
  const [deleteClientAccess, { isLoading: isLoadingDeleteClientAccess }] = useDeleteClientAccessMutation();
  const { data: clientAccess = [] } = useGetClientAccessQuery(activeClient?.ext_client_id ?? '', { skip: !activeClient?.ext_client_id });
  const { data: clientList, refetch: refetchClientList } = useGetOrganizationClientsQuery();
  const { data: companies = [], isLoading: isLoadingCompanies } = useGetCompaniesQuery();
  const { data: accounts = [], isLoading: isLoadingAccounts } = useGetAccountsQuery({});
  const { data: campaigns = [], isLoading: isLoadingCampaigns } = useGetCampaignsQuery({});
  const [uploadImage] = useUploadImageMutation();


  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>(!isCreate ? activeClient?.companies ?? [] : []);
  const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);
  const [clientName, setClientName] = useState<string>(!isCreate ? activeClient?.title ?? '' : '');
  const [clientImage, setClientImage] = useState<string | null>(!isCreate ? activeClient?.brand ?? null : null);
  const [autoAccounts, setAutoAccounts] = useState<boolean>(activeClient?.auto_add_accounts ?? true);
  const [autoCampaigns, setAutoCampaigns] = useState<boolean>(activeClient?.auto_add_campaigns ?? true);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [showCustomize, setShowCustomize] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);

  useEffect(() => {
    if (!isCreate && clientAccess.length > 0 && accounts.length > 0 && campaigns.length > 0) {
      const accessAccountIds = clientAccess.map((access) => access.accounts).flat().map((account) => account.account_id);

      const matchingAccounts = accounts.filter((account) =>
        accessAccountIds.includes(account.ext_account_id)
      );

      const matchingCampaigns = campaigns.filter((campaign) =>
        accessAccountIds.includes(campaign.account.id)
      );

      // console.log(matchingAccounts);
      setSelectedCompanies(companies.filter((company) => activeClient?.companies.some((c) => c.id === company.id)));
      setSelectedAccounts(matchingAccounts);
      setSelectedCampaigns(matchingCampaigns);
    }
  }, [clientAccess, accounts, campaigns, isCreate, activeClient]);

  useEffect(() => {
    if (selectedCompanies.length === 0 ) {
      setFilteredAccounts(accounts);
    } else {
      setClientName(selectedCompanies.map((company) => company.company_name).join(', '));
      const accessAccountIds = clientAccess.map((access) => access.accounts).flat().map((account) => account.account_id);

      const selectedAccounts = accounts.filter((account) =>
        accessAccountIds.includes(account.ext_account_id) || selectedCompanies.some((company) => company.id === account.company)
      );

      setSelectedAccounts(selectedAccounts);
      setFilteredAccounts(selectedAccounts);
    }
  }, [selectedCompanies]);

  useEffect(() => {
    if (selectedAccounts.length === 0) {
      setSelectedCampaigns([]);
      setFilteredCampaigns(campaigns);
    } else {
      setSelectedCampaigns(campaigns.filter((campaign) => selectedAccounts.some((account) => account.id === campaign.account.id)));
      setFilteredCampaigns(campaigns.filter((campaign) => selectedAccounts.some((account) => account.id === campaign.account.id)));
    }

  }, [selectedAccounts, filteredAccounts]);


  /**
   * Updates client account access by adding accounts and their associated campaigns
   * @param clientId - The client ID to update accounts for
   * @returns Promise<boolean> indicating success
   */
  const updateClientAccounts = async(clientId: string): Promise<boolean> => {
    if (!clientId) {
      return true; // Nothing to update
    }

    try {
      // start by clearing all client access, and then rebuild after
      const deleteResults = await deleteClientAccess({
        ext_client_id: clientId,
      });

      const results = await Promise.allSettled(
        selectedAccounts.map((account) =>
          updateClientAccess({
            ext_client_id: clientId,
            source: account.source,
            account: account.ext_account_id,
            campaigns: selectedCampaigns
              .filter((campaign) => campaign.account.id === account.id)
              .map((campaign) => campaign.ext_campaign_id),
          })
        )
      );

      const failures = results.filter((result) => result.status === 'rejected');

      if (failures.length > 0) {
        console.error('Failed to update some accounts:', failures);
        dispatch(
          showNotification({
            summary: `Failed to update ${failures.length} account(s)`,
            theme: 'warning',
          })
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating client accounts:', error);
      dispatch(
        showNotification({
          summary: 'Error updating client accounts',
          theme: 'warning',
        })
      );
      return false;
    }
  };

  /**
   * Creates a new client with just the title, then updates it with additional data
   */
  const handleCreate = async(): Promise<void> => {
    try {
      const response = await createClient({ title: clientName, views: [FALLBACK_DEFAULT_CLIENT] });

      if (!('data' in response)) {
        dispatch(
          showNotification({
            summary: 'Error creating client',
            theme: 'warning',
          })
        );
        return;
      }

      const newClient = response.data;
      setClient(newClient);

      // If there's additional data to add, update the newly created client directly
      if (selectedCompanies.length > 0 || selectedAccounts.length > 0) {
        // Update the client with companies and settings
        const updateResponse = await updateClient({
          clientId: newClient.ext_client_id,
          data: {
            title: clientName,
            auto_add_accounts: autoAccounts,
            auto_add_campaigns: autoCampaigns,
            companies_data: selectedCompanies.map((company) => ({
              id: company.id})),
          },
        });

        if (!('data' in updateResponse)) {
          dispatch(
            showNotification({
              summary: 'Client created but failed to update details',
              theme: 'warning',
            })
          );
          return;
        }

        // Update account access
        const accountsUpdated = await updateClientAccounts(newClient.ext_client_id);

        setClient(updateResponse.data);

        if (accountsUpdated) {
          dispatch(
            showNotification({
              summary: 'Client created successfully',
              theme: 'success',
            })
          );
        } else {
          dispatch(
            showNotification({
              summary: 'Client created, but some accounts failed',
              theme: 'warning',
            })
          );
        }
      } else {
        dispatch(
          showNotification({
            summary: 'Client created successfully',
            theme: 'success',
          })
        );
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Error creating client:', error);
      dispatch(
        showNotification({
          summary: 'Failed to create client',
          theme: 'warning',
        })
      );
    }
  };

  const handlePreview = () => {
    navigate(`/reports-old?previewClientId=${activeClient?.ext_client_id}&previewClientName=${encodeURIComponent(activeClient?.title ?? '')}`);
  };

  const handleReset = () => {
    if (!activeClient) {
      return;
    }

    confirmDialog({message: 'Are you sure you want to reset the client?',
      header: 'Reset Client',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptClassName: 'ml-2 bg-green-501 text-white p-2 rounded-full w-1/4',
      rejectClassName: 'mr-2 bg-gray-600 text-white p-2 rounded-full w-1/4',
      accept: () => {
        setSelectedCompanies([]);
        setSelectedAccounts([]);
        setSelectedCampaigns([]);
        setClientName('Unknown Client');
        setHasChanges(true);
        handleUpdate();
      },
      reject: () => {
        return;
      },
    });
  };

  /**
   * Updates an existing client's details, companies, and account access
   */
  const handleUpdate = async(): Promise<void> => {
    try {
      // Validation
      if (!clientName?.trim()) {
        dispatch(
          showNotification({
            summary: 'Client name is required',
            theme: 'warning',
          })
        );
        return;
      }

      // If no client exists yet, create it first
      if (!activeClient?.ext_client_id) {
        await handleCreate();
        return;
      }

      // Update client details
      const response = await updateClient({
        clientId: activeClient.ext_client_id,
        data: {
          title: clientName,
          auto_add_accounts: autoAccounts,
          auto_add_campaigns: autoCampaigns,
          companies_data: selectedCompanies.map((company) => ({
            id: company.id,
          })),
        },
      });

      if (!('data' in response)) {
        dispatch(
          showNotification({
            summary: 'Error updating client',
            theme: 'warning',
          })
        );
        return;
      }

      // Update account access
      const accountsUpdated = await updateClientAccounts(activeClient.ext_client_id);

      // Show success message
      if (accountsUpdated) {
        dispatch(
          showNotification({
            summary: 'Client updated successfully',
            theme: 'success',
          })
        );
        await refetchClientList();
        const updatedClient = clientList?.find((client) => client.ext_client_id === activeClient.ext_client_id);
        if (updatedClient) {
          setClient(updatedClient);
        }
        setHasChanges(false);
      } else {
        dispatch(
          showNotification({
            summary: 'Client updated, but some accounts failed',
            theme: 'warning',
          })
        );
      }
    } catch (error) {
      console.error('Error updating client:', error);
      dispatch(
        showNotification({
          summary: 'An unexpected error occurred',
          theme: 'warning',
        })
      );
    }
  };

  const handleUpload = async(uploadedImage: File) => {
    if (uploadedImage && activeClient) {
      if (uploadedImage.size > 1000 * 1024) { // 1MB in bytes
        dispatch(showNotification({
          summary: 'File size exceeds 1MB limit',
          theme: 'warning',
          sticky: true,
        }));
        setUploadedImage(null);
        setClientImage(null);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', uploadedImage);
        formData.append('type', 'client');
        formData.append('uuid', activeClient.ext_client_id);
        const response = await uploadImage({ formData, uuid: activeClient.ext_client_id }).unwrap();
        setClientImage(response.item.url);
        await updateClient({
          clientId: activeClient.ext_client_id,
          data: {
            title: clientName,
            brand: response.item.url,
          },
        });
        setUploadedImage(null);
      } catch (error) {
        console.error('Error uploading image:', error);
        dispatch(showNotification({
          summary: 'Error uploading image. Please try again.',
          theme: 'warning',
          sticky: true,
        }));
      }
    }
  };

  return (
    <div className='w-full bg-gray-200'>
      <ConfirmDialog />
      <ClientCreationMenuBar
        showCustomize={showCustomize}
        setShowCustomize={setShowCustomize}
        handleSave={handleUpdate}
        handleReset={handleReset}
        handlePreview={handlePreview}
        hasChanges={hasChanges}
      />

      {showCustomize ? (
        <div className='pt-8 border border-2 border-dashed border-yellow-500'>
          <ClientViewCustomize />
        </div>
      ) : (
        <>
          {!isCreate && <ClientUserTable />}

          <div className="flex flex-row justify-between overflow-hidden p-4 pb-0">

            {/* Company Selector (optional) */}
            <CompanyMultiSelector
              isLoading={isLoadingCompanies}
              companies={companies}
              selectedCompanies={selectedCompanies}
              onCompanySelect={(company) => {
                setHasChanges(true);
                if (selectedCompanies.some((c) => c.id === company.id)) {
                  setSelectedCompanies(selectedCompanies.filter((c) => c.id !== company.id));
                } else {
                  setSelectedCompanies([...selectedCompanies, company]);
                }
              }}
            />

            <div className='flex flex-row items-center w-[15px]'>
              <ArrowRight className='size-4' />
            </div>

            {/* Account Selector */}
            <AccountMultiSelector
              isLoading={isLoadingAccounts}
              accounts={accounts}
              selectedAccounts={selectedAccounts}
              onAccountSelect={(account) => {
                setHasChanges(true);
                if (selectedAccounts.some((a) => a.id === account.id)) {
                  setSelectedAccounts(selectedAccounts.filter((a) => a.id !== account.id));
                } else {
                  setSelectedAccounts([...selectedAccounts, account]);
                }
              }}
              companies={companies}
            />


            <div className='flex flex-row items-center justify-center w-[15px]'>
              <ArrowRight className='size-4' />
            </div>

            {/* Campaign Selector */}
            <CampaignMultiSelector
              isLoading={isLoadingCampaigns}
              campaigns={campaigns}
              selectedCampaigns={selectedCampaigns}
              onCampaignSelect={(campaign) => {
                setHasChanges(true);
                if (selectedCampaigns.some((c) => c.id === campaign.id)) {
                  setSelectedCampaigns(selectedCampaigns.filter((c) => c.id !== campaign.id));
                } else {
                  setSelectedCampaigns([...selectedCampaigns, campaign]);
                }
              }}
              companies={companies}
            />

          </div>
          <div className='flex flex-row'>

            {/* Client Details */}
            <div className='flex flex-row mt-4 w-full p-4 pt-0 gap-2'>
              <div className='flex flex-col w-1/2 bg-white rounded-md overflow-hidden shadow-sm h-fit'>
                <div className='flex flex-row items-center bg-gray-201 font-medium text-black p-2 gap-2 pl-4 mb-2 pb-1'>
                  <CircleUser className='size-4' />
                  Client Details
                </div>

                <div className='flex flex-row items-center'>
                  <input
                    type="file"
                    accept="image/*"
                    id="client-image-upload"
                    className="hidden"
                    disabled={!activeClient}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setClientImage(URL.createObjectURL(e.target.files[0]));
                        handleUpload(e.target.files[0]);
                      }
                    }}
                  />
                  <label
                    htmlFor="client-image-upload"
                    className='flex w-full rounded-md p-4 m-4 mt-0 items-center justify-center cursor-pointer relative rounded-2xl border-2 border-dashed p-6 transition-colors border-gray-300'
                  >
                    {clientImage ? (
                      <img
                        src={clientImage}
                        alt="Client"
                        className="max-h-[100px] object-contain"
                      />
                    ) : (
                      <div className='flex flex-col items-center justify-center'>
                        <Image className='size-6 mr-1' />
                        <span className='text-sm font-bold'>
                          Click to upload a client image
                        </span>
                        <span className='text-gray-500 text-xs'>
                          PNG or JPG up to 2MB
                        </span>
                      </div>
                    )}
                  </label>
                </div>

                <div className='flex flex-row items-center px-4 mb-2'>
                  <div className='flex flex-col w-full'>
                    <span className='text-xs text-gray-500'>
                      Client Name
                    </span>
                    <input
                      value={clientName}
                      onChange={(e) => {setClientName(e.target.value); setHasChanges(true);}}
                      placeholder='Name'
                      className='w-full focus:outline-none bg-transparent border-b border-gray-400 px-2 mr-2 mb-1 text-sm text-gray-500'
                    />
                  </div>
                </div>

                <div className='flex flex-row items-center'>
                  <div className='flex w-full justify-start'>
                    <div className='flex flex-row w-full'>
                      <div className='flex flex-row items-center w-full justify-between'>
                        <Button
                          variant='ghost'
                          size='sm'
                          disabled={selectedCompanies.length === 0}
                          className={`flex-1 justify-center bg-green-501 text-white rounded-none ${autoAccounts && selectedCompanies.length > 0 ? 'bg-green-501 text-white' : 'bg-gray-200 text-gray-500'}`}
                          onClick={() => {setHasChanges(true);
                            setAutoAccounts(!autoAccounts);}}
                        >
                          {autoAccounts && selectedCompanies.length > 0 ? <Check className='size-4' /> : <X className='size-4' />}
                          Auto+ Accounts
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          disabled={selectedAccounts.length === 0}
                          className={`flex-1 justify-center bg-gray-200 text-white rounded-none ${autoCampaigns && selectedAccounts.length > 0 ? 'bg-green-501 text-white' : 'bg-gray-200 text-gray-500'}`}
                          onClick={() => {setHasChanges(true);
                            setAutoCampaigns(!autoCampaigns);}}
                        >
                          {autoCampaigns && selectedAccounts.length > 0 ? <Check className='size-4' /> : <X className='size-4' />}
                          Auto+ Campaigns
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex flex-col w-1/2 bg-white rounded-md overflow-hidden shadow-sm'>
                <div className='flex flex-row mb-2 items-center bg-gray-201 font-medium text-black p-2 gap-2 pl-4 mb-2 pb-1'>
                  <ReceiptText className='size-4' />
                  Client Overview
                </div>
                <div className='flex flex-row items-center'>
                  <div className='flex flex-col w-full px-4'>
                    <div className='flex flex-row items-center text-xs text-gray-500 border-b border-green-501 w-fit mb-1'>
                      <Megaphone className='size-3 mr-1' />
                      Accounts
                    </div>
                    <div className='flex flex-row flex-wrap'>
                      {selectedAccounts.map((account) => (
                        <div
                          key={account.id}
                          className='flex flex-row items-center text-xs text-gray-500 bg-gray-200 rounded-full px-2 pr-1 py-0.5 mr-1 mb-1 flex-shrink-0'
                        >
                          {account.title}
                          <X
                            className='size-3 ml-1 cursor-pointer'
                            onClick={() => {setHasChanges(true);
                              setSelectedAccounts(selectedAccounts.filter((a) => a.id !== account.id));}}
                          />
                        </div>
                      ))}
                      {selectedAccounts.length === 0 ? (
                        <div className='text-xs text-gray-500 px-1 pl-2 py-0.5 mr-1 mb-1 flex-shrink-0'>
                          No accounts selected
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className='flex flex-row items-center mt-2'>
                  <div className='flex flex-col w-full px-4'>
                    <div className='flex flex-row items-center text-xs text-gray-500 border-b border-yellow-500 w-fit mb-1'>
                      <File className='size-3 mr-1' />
                      Campaigns
                    </div>
                    <div className='flex flex-row flex-wrap'>
                      {selectedCampaigns.map((campaign) => (
                        <div
                          key={campaign.id}
                          className='flex flex-row items-center text-xs text-gray-500 bg-gray-200 rounded-full px-2 pr-1 py-0.5 mr-1 mb-1 flex-shrink-0'
                        >
                          {campaign.title}
                          <X
                            className='size-3 ml-1 cursor-pointer'
                            onClick={() => {setHasChanges(true);
                              setSelectedCampaigns(selectedCampaigns.filter((c) => c.id !== campaign.id));}}
                          />
                        </div>
                      ))}
                      {selectedCampaigns.length === 0 ? (
                        <div className='text-xs text-gray-500 px-1 pl-2 py-0.5 mr-1 mb-1 flex-shrink-0'>
                          No campaigns selected
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};
export default ClientCreationComponent;
